import { useState, useEffect, memo, useCallback, useContext, Suspense } from 'react';
import { DialogContentText } from '@mui/material';
import { areControllerPropsEqual } from '../utilities/utils';
import { ControllerContext, SelectedSettingsContext } from '../ts/Contexts';
import StatefulSettings from '../controllers/StatefulSettings';
import LazyLoader from '../controls/lazyLoader';
import { Check, ExpandLess, ExpandMore, Visibility, Close } from '@mui/icons-material';
import { green } from '@mui/material/colors';
import { ControlRecord, ISettingsItem, ITaskItem } from '../ts/Definitions';
import QuickTaskProfile from '../itemViewers/QuickTaskProfile';
import { ItemTypes, ProfileRole } from '../ts/enums';
import Emitter, { ShowAdditionDialog } from '../utilities/Emitter';
import { lazyWithRetry } from '../utilities/lazyLoaderUtils';
import { MessageType, NotificationType, sendMessageToMember } from '../controllers/messaging';


interface TaskPickerState {
    open: boolean,
    text: string,
    tasks: ITaskItem[],
    callback: (ok: boolean, task: ISettingsItem | undefined) => void,
    title?: string,
}

const TaskPicker = lazyWithRetry(() => (import('../dialogs/TaskPicker')));


const UnownedMemberTaskAllocator = (): JSX.Element | null => {
    const controller = useContext(ControllerContext);
    const settings = useContext(SelectedSettingsContext);
    const [recentSessions, setRecentSessions] = useState<ControlRecord[]>([]);
    const [taskPickerState, setTaskPickerState] = useState<TaskPickerState>();
    const refreshData = useCallback(() => {
        if (!settings) return; // still waiting for them
        setRecentSessions(Object.values(settings.getActiveSessions())
            .filter(c => c.role !== ProfileRole.Owner
                && c.isOwnedBySelf()));
    }, [settings]);

    useEffect(() => {
        controller.subscribeToLogOn(refreshData);
        return () => {
            controller.unsubscribeToLogOn(refreshData);
        };
    }, [controller, refreshData]);

    useEffect(() => {
        settings.subscribeToViewChanges(refreshData);
        settings.subscribeToAllControlRecords(refreshData);
        refreshData();
        return () => {
            settings.unsubscribeToAllControlRecords(refreshData);
        };
    }, [refreshData, settings]);

    const handleAssignTask = (profile: ControlRecord) => {
        void controller.getSettingsAndThen(profile.userid, (settings) => {
            if (!settings.canPerformSomeTasksForToday()) {
                Emitter.emitNotification({ message: 'Member is unavailable for tasks today', notificationType: NotificationType.General })
                return;
            }
            pickTaskAndThen(settings, (ok, task) => {
                if (ok) {
                    Emitter.emit(ShowAdditionDialog, settings, ItemTypes.task, task, undefined, undefined, (ok: boolean) => {
                        if (!ok || !task) return;
                        const loggedOnUser = controller.getLoggedOnUserSettings();
                        const isGuest = loggedOnUser.getControl().userid === import.meta.env.VITE_GUEST_NAME;
                        const username = loggedOnUser.getCurrentCommunityProfile().username;
                        if (isGuest) {
                            sendMessageToMember(MessageType.UnownedTaskAllocated,loggedOnUser,
                                `You have been assigned task ${task.name} by a guest who is browsing the site. Picking you up like a toy they have played with you and thrown you to the side. Comply with their wishes.`,
                                `A guest has assigned you ${task.name} Task!`,
                                [profile],  { userid: import.meta.env.VITE_GUEST_NAME, username: 'A Guest' });
    
                        } else {
                            sendMessageToMember(MessageType.UnownedTaskAllocated, loggedOnUser,
                                `You have been assigned task ${task.name} by ${username} who is an owner in the system. Respond back to them by replying to this message to thank them for the task.`,
                                `${username} assigned you ${task.name} Task!`,
                                [profile]);
    
                        }
                    });
                }
            }, 'Pick the task from the list supported for this member to apply to their session. They will get a message telling them it was you!', 'Pick a Task')
        });
    }

    const pickTaskAndThen = useCallback((settings: StatefulSettings,
        callback: (ok: boolean, task: ISettingsItem | undefined) => void,
        text: string, title: string) => {
        let tasks = settings.getTasks(undefined, true, true).sort((x, y) => x.name?.trimStart().localeCompare(y.name?.trimStart()));
        tasks = tasks.filter(t => settings.canAddTask(t).isOk);
        setTaskPickerState({
            callback: (ok, task) => {
                setTaskPickerState(undefined);
                callback(ok, task);
            },
            text,
            open: true,
            tasks,
            title
        });
    }, []);

    const recentSessionsTile = <div className="recentSessionTile" style={{ background: '#FFF' }}>
        <p className='memberElementTitle' onClick={() => settings.showSummary('minimiseRecentSessionsSummary')}><Check style={{ color: green[500], paddingRight: '10px', width: '36px', height: '36px' }} />Active Members
            <span className='expandCollapseSection'>
                {settings.canShowSummary('minimiseRecentSessionsSummary') && <ExpandLess className='expandCollapseSectionButton' onClick={(args) => {
                    settings.hideSummary('minimiseRecentSessionsSummary');
                    args.stopPropagation();
                }} />}
                {!settings.canShowSummary('minimiseRecentSessionsSummary') && <ExpandMore className='expandCollapseSectionButton orange' onClick={() => settings.showSummary('minimiseRecentSessionsSummary')} />}
                {!settings.canShowSummary('recentSessionTile') && <span onClick={() => settings.showSummary('recentSessionTile')} className='showThisSummary'>{'[Show Info '}<Visibility className='hideSummaryButton' />{']'}</span>}
            </span>
        </p>
        {settings.canShowSummary('recentSessionTile') &&
            <DialogContentText className='dialogContentTextHeader memberElementDescription'>{`Here are users who have recently been active. 
        
        Why don't you practice assigning a task to one of these members? All of these members are unowned and so an extra task for today won't be a problem!`}
                <span onClick={() => settings.hideSummary('recentSessionTile')} className='hideThisText'>{'[Hide'}<Close className='hideSummaryButton' />{']'}</span>
            </DialogContentText>}
        {recentSessions.map((s, idx) => <QuickTaskProfile
            profile={s}
            key={idx}
            assignTaskCallback={handleAssignTask}
        />)}
    </div>;

    if (!settings || recentSessions.length === 0) return null;

    return <Suspense fallback={<LazyLoader
        status="Fetching Member Session..."
        delay={100} />}>
        <div>

            {taskPickerState && (<TaskPicker
                tasks={taskPickerState.tasks}
                callback={taskPickerState.callback}
            />)}
            {recentSessionsTile}
        </div>
    </Suspense>
};

export default memo(UnownedMemberTaskAllocator, areControllerPropsEqual)